import React, { useMemo } from "react";
import { Alert, Stack, Typography } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";

import { useStationListWithCache } from "../../components/StationHookFormSelect/useStationListWithCache";
import { isSealDip } from "./helper";
import { filterDipMovementsByLocation } from "../../constants/dips";

const DipsWarningBanner = ({ basePath, locationId }) => {
  const { control } = useFormContext();
  const dips = useWatch({ control, name: `${basePath}.routing` }) || [];
  const { stations = [] } = useStationListWithCache();

  const stationById = useMemo(() => {
    const map = new Map();
    stations.forEach((s) => map.set(s.id, s));
    return map;
  }, [stations]);

  const allowedMovementIds = useMemo(
    () => new Set(filterDipMovementsByLocation(locationId).map((m) => m.id)),
    [locationId],
  );

  const issues = useMemo(() => {
    if (!dips.length) return [];
    const out = [];

    const lastIdx = dips.length - 1;
    const lastDip = dips[lastIdx];
    if (lastDip && !isSealDip(lastDip)) {
      out.push("Last dip must be a Seal Dip.");
    }

    let robotCount = 0;
    let handCount = 0;

    dips.forEach((dip, idx) => {
      const num = idx + 1;
      const station = dip?.stationId ? stationById.get(dip.stationId) : null;
      const isRobot = !!station?.robotic;
      // Sand/movement live in the per-dip `config` blob now.
      const materialId = dip?.config?.materialId;
      const movementId = dip?.config?.movementId;
      if (station) {
        if (isRobot) robotCount += 1;
        else handCount += 1;
      }

      // Seal dip must not have a sand selected.
      if (isSealDip(dip) && materialId) {
        out.push(`Row ${num}: Seal dip cannot have a sand selected.`);
      }

      // Only the last dip is allowed to be a seal dip.
      if (idx !== lastIdx && isSealDip(dip)) {
        out.push(`Row ${num}: Only the last dip may be a Seal Dip.`);
      }

      // Robot stations require a movement.
      if (isRobot && !movementId) {
        out.push(
          `Row ${num}: Robot station requires a Movement to be selected.`,
        );
      }

      // Inverse: a movement can only be paired with a robotic station.
      if (movementId && station && !isRobot) {
        out.push(
          `Row ${num}: Movement is only valid with a robotic station — clear the movement or pick a robot station.`,
        );
      }

      // Movement chosen but no station picked at all.
      if (movementId && !station) {
        out.push(
          `Row ${num}: Movement requires a robot station to be selected.`,
        );
      }

      // Movement must belong to the current location's robot set.
      if (
        isRobot &&
        movementId &&
        allowedMovementIds.size > 0 &&
        !allowedMovementIds.has(movementId)
      ) {
        out.push(
          `Row ${num}: Selected Movement is not available at this location.`,
        );
      }
    });

    if (robotCount > 0 && handCount > 0) {
      out.push(
        "Mixing hand and robot dip stations in the same recipe — verify this is intentional.",
      );
    }

    return out;
  }, [dips, stationById, allowedMovementIds]);

  if (!issues.length) return null;

  return (
    <Alert severity="warning" sx={{ alignItems: "flex-start" }}>
      <Stack gap="2px">
        <Typography variant="boldCaption" fontSize={12}>
          Review before saving
        </Typography>
        {issues.map((msg, i) => (
          <Typography key={i} variant="caption" fontSize={11}>
            • {msg}
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
};

export default DipsWarningBanner;
